// A send that never answers must not hold the queue (#614). Sync runs one send
// at a time and every trigger (Sync now, the minute timer, coming back to the
// app) joins the run already going, so a request that never settles — an iPad
// that left the app mid-request, a token request that never came back — kept
// the spinner going and every change waiting until the app was closed.
//
// So each REQUEST of a send has a deadline, well past the SWA gateway's ~45 s
// (after which no answer can come): at it, the request is aborted and the send
// fails, so the run ends as a failed attempt; the next run sends the change
// again, which is safe (records are saved by id, a photo already uploaded isn't
// uploaded twice, and an update that did land is recognised rather than taken
// for a conflict: outbox.ts ownEarlierSave). The device's own bookkeeping after
// an answer has no deadline: it always finishes, so a run never ends with an
// answer half written, and a late answer writes nothing (its send already
// failed). Pure.

/** Longer than the SWA gateway lets any request run, with room for a slow photo upload. */
const SEND_DEADLINE_MS = 90_000;

const NO_ANSWER = 'The server didn’t answer in time. Your changes are kept on this device and will be sent again.';

/** A request given up at its deadline: sync ends the run and tries again later. */
export class SendTimedOut extends Error {
  constructor() {
    super(NO_ANSWER);
    this.name = 'SendTimedOut';
  }
}

/** One send's requests: each is aborted through `signal`, and `inTime` gives it its deadline. */
export interface SendRequests {
  signal: AbortSignal;
  /** The request's answer or failure; SendTimedOut, the request aborted, if neither came within the deadline. */
  inTime<T>(request: Promise<T>): Promise<T>;
}

/**
 * Deadlines for one send's requests. Once one has been given up, the send's
 * signal is aborted, so a request still to start (a token that never came)
 * fails at once, and any later one is given up straight away.
 */
export function sendRequests(ms: number = SEND_DEADLINE_MS): SendRequests {
  const controller = new AbortController();
  async function inTime<T>(request: Promise<T>): Promise<T> {
    if (controller.signal.aborted) throw new SendTimedOut();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new SendTimedOut());
      }, ms);
    });
    try {
      return await Promise.race([request, deadline]);
    } finally {
      clearTimeout(timer);
    }
  }
  return { signal: controller.signal, inTime };
}
